import React from 'react';
import { List, Input, Tabs, Badge, Avatar, Typography, Empty, Spin, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { WAConversation } from '../../types/whatsapp';

const { Text } = Typography;

export type WAFilter = 'mine' | 'unassigned' | 'all';

interface ConversationListProps {
  conversations: WAConversation[];
  loading: boolean;
  selectedId: string | null;
  filter: WAFilter;
  canAssign: boolean;
  search: string;
  onFilterChange: (filter: WAFilter) => void;
  onSearchChange: (value: string) => void;
  onSelect: (conversation: WAConversation) => void;
}

export const isWindowOpen = (conversation: WAConversation): boolean => {
  if (!conversation.window_expires_at) return false;
  return dayjs(conversation.window_expires_at).isAfter(dayjs());
};

const formatTime = (value?: string) => {
  if (!value) return '';
  const d = dayjs(value);
  if (d.isSame(dayjs(), 'day')) return d.format('HH:mm');
  return d.format('DD/MM');
};

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  loading,
  selectedId,
  filter,
  canAssign,
  search,
  onFilterChange,
  onSearchChange,
  onSelect,
}) => {
  const tabItems = [
    { key: 'mine', label: 'Saya' },
    ...(canAssign
      ? [
          { key: 'unassigned', label: 'Belum di-assign' },
          { key: 'all', label: 'Semua' },
        ]
      : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs
        activeKey={filter}
        onChange={(key) => onFilterChange(key as WAFilter)}
        items={tabItems}
        style={{ marginBottom: 0 }}
      />
      <Input.Search
        placeholder="Cari nama atau nomor..."
        allowClear
        defaultValue={search}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Spin spinning={loading}>
          {conversations.length === 0 && !loading ? (
            <Empty description="Tidak ada percakapan" style={{ marginTop: 40 }} />
          ) : (
            <List
              dataSource={conversations}
              rowKey="id"
              renderItem={(item) => {
                const windowOpen = isWindowOpen(item);
                const title = item.contact_name || item.wa_phone;
                return (
                  <List.Item
                    onClick={() => onSelect(item)}
                    style={{
                      cursor: 'pointer',
                      padding: '10px 12px',
                      background: selectedId === item.id ? '#e6f4ff' : undefined,
                      borderRadius: 6,
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <Badge
                          dot
                          color={windowOpen ? '#52c41a' : '#bfbfbf'}
                          offset={[-2, 24]}
                        >
                          <Avatar icon={<UserOutlined />} />
                        </Badge>
                      }
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Text strong style={{ maxWidth: 140 }} ellipsis>
                            {title}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {formatTime(item.last_message_at)}
                          </Text>
                        </div>
                      }
                      description={
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.wa_phone}
                            </Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {item.opted_out_at && (
                                <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                                  Opt-out
                                </Tag>
                              )}
                              {item.unread_count > 0 && <Badge count={item.unread_count} size="small" />}
                            </div>
                          </div>
                          {item.lead && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                              <Tag color="blue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                                Lead: {item.lead.full_name}
                              </Tag>
                              {item.lead.lead_follow_up_status?.name && (
                                <Tag color="geekblue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                                  {item.lead.lead_follow_up_status.name}
                                </Tag>
                              )}
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default ConversationList;
